import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiLock, FiEye, FiMail, FiCalendar, FiDatabase, FiGlobe } = FiIcons;

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: FiEye,
      title: "1. Information We Collect",
      content: `We collect information in several ways:

**Information You Provide:**
• Contact details (name, email, phone number)
• Company information
• Project requirements and specifications
• Communication preferences
• Payment information (processed securely through third-party providers)

**Information We Collect Automatically:**
• Website usage data (pages visited, time spent, clicks)
• Device information (browser type, operating system, IP address)
• Cookies and similar tracking technologies
• Performance and analytics data

**Information from Third Parties:**
• Social media profile information (if you connect accounts)
• Professional networking platforms
• Business verification services`
    },
    {
      icon: FiDatabase,
      title: "2. How We Use Your Information",
      content: `We use your information for:

**Service Delivery:**
• Providing AI-powered development services
• Project management and communication
• Technical support and maintenance
• Quality assurance and testing

**Business Operations:**
• Processing payments and managing accounts
• Sending service updates and notifications
• Improving our services and user experience
• Compliance with legal obligations

**Marketing (with your consent):**
• Sending newsletters and product updates
• Sharing relevant case studies and insights
• Promotional communications about new services
• Industry event invitations

**Legal and Security:**
• Protecting against fraud and unauthorized access
• Complying with legal requirements
• Enforcing our terms of service
• Investigating security incidents`
    },
    {
      icon: FiGlobe,
      title: "3. Information Sharing and Disclosure",
      content: `We may share your information in limited circumstances:

**Service Providers:**
• Cloud hosting providers (AWS, Google Cloud)
• Payment processors (Stripe, PayPal)
• Email service providers
• Analytics platforms (Google Analytics)
• Customer support tools

**Legal Requirements:**
• When required by law or court order
• To protect our rights and property
• To investigate fraud or security issues
• In connection with legal proceedings

**Business Transfers:**
• In case of merger, acquisition, or sale of assets
• During business restructuring
• With successor entities (with notice to users)

**With Your Consent:**
• When you explicitly authorize sharing
• For testimonials and case studies (anonymized)
• For partnership opportunities`
    },
    {
      icon: FiLock,
      title: "4. Data Security and Protection",
      content: `We implement comprehensive security measures:

**Technical Safeguards:**
• End-to-end encryption for data transmission
• Encrypted storage for sensitive information
• Regular security audits and penetration testing
• Multi-factor authentication for admin access
• Automated backup and disaster recovery

**Organizational Measures:**
• Employee training on data protection
• Strict access controls and permissions
• Regular security awareness programs
• Incident response procedures
• Third-party security assessments

**Data Retention:**
• We retain data only as long as necessary
• Automatic deletion of expired data
• Secure disposal of physical media
• Regular data purging procedures`
    },
    {
      icon: FiShield,
      title: "5. Your Rights and Choices",
      content: `Under GDPR, CCPA, and other privacy laws, you have:

**Access Rights:**
• Request copies of your personal data
• Information about how we process your data
• Details about data sharing and retention

**Control Rights:**
• Update or correct your information
• Delete your personal data (right to erasure)
• Restrict or object to processing
• Data portability (receive data in structured format)

**Communication Preferences:**
• Opt-out of marketing communications
• Choose notification preferences
• Update contact methods
• Unsubscribe from newsletters

**Cookie Controls:**
• Manage cookie preferences
• Opt-out of non-essential cookies
• Clear stored cookies and data
• Configure browser settings`
    },
    {
      icon: FiGlobe,
      title: "6. International Data Transfers",
      content: `As a global service provider:

• We may transfer data across borders for service delivery
• All transfers comply with applicable privacy laws
• We use appropriate safeguards (Standard Contractual Clauses)
• Data is protected regardless of location
• We maintain data processing agreements with all vendors
• Regular compliance audits ensure ongoing protection`
    },
    {
      title: "7. Cookies and Tracking Technologies",
      content: `We use various technologies to enhance your experience:

**Essential Cookies:**
• Authentication and session management
• Security and fraud prevention
• Service functionality and features
• Load balancing and performance

**Analytics Cookies:**
• Website usage statistics
• Performance monitoring
• User behavior analysis
• Service improvement insights

**Marketing Cookies:**
• Personalized content delivery
• Advertising effectiveness measurement
• Cross-platform tracking (with consent)
• Social media integration

You can control cookies through our cookie consent manager or browser settings.`
    },
    {
      title: "8. Children's Privacy",
      content: `Our services are not intended for children under 16:

• We do not knowingly collect data from minors
• If we discover underage data collection, we delete it immediately
• Parents can contact us to request data removal
• We comply with COPPA and similar regulations
• Age verification may be required for certain services`
    },
    {
      title: "9. Changes to This Policy",
      content: `We may update this policy periodically:

• Material changes will be announced via email
• Updated policy will be posted on our website
• Continued use constitutes acceptance
• You can review previous versions upon request
• Significant changes require explicit consent`
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center mb-6">
              <SafeIcon icon={FiShield} className="h-12 w-12 text-blue-600 mr-4" />
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                Privacy Policy
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-4">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            <div className="flex items-center justify-center text-sm text-gray-500">
              <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
              <span>Last updated: December 2024</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-12">
              <div className="flex items-start">
                <SafeIcon icon={FiLock} className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Our Commitment to Privacy</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    At aiximo, we are committed to protecting your privacy and ensuring the security of your personal information. 
                    This Privacy Policy explains our practices regarding the collection, use, and disclosure of information through our services.
                  </p>
                </div>
              </div>
            </div>

            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="mb-12"
              >
                <div className="flex items-center mb-4">
                  {section.icon && (
                    <SafeIcon icon={section.icon} className="h-6 w-6 text-blue-600 mr-3" />
                  )}
                  <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                </div>
                <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}

            {/* GDPR Rights */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-green-50 border border-green-200 rounded-2xl p-8 mb-12"
            >
              <h2 className="text-2xl font-bold text-green-900 mb-6 flex items-center">
                <SafeIcon icon={FiShield} className="h-6 w-6 mr-3" />
                GDPR Compliance
              </h2>
              <p className="text-green-800 mb-4">
                We are fully compliant with the General Data Protection Regulation (GDPR) and other international privacy laws. 
                If you are located in the EU, you have additional rights:
              </p>
              <ul className="text-green-800 space-y-2">
                <li>• <strong>Right to Access:</strong> Request information about your personal data</li>
                <li>• <strong>Right to Rectification:</strong> Correct inaccurate personal data</li>
                <li>• <strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                <li>• <strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                <li>• <strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
                <li>• <strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              </ul>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gray-50 border border-gray-200 rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Us About Privacy</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  For privacy-related questions, requests, or concerns, please contact our Data Protection Officer:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-700">
                    <SafeIcon icon={FiMail} className="h-5 w-5 mr-3 text-blue-600" />
                    <a href="mailto:privacy@aiximo.com" className="text-blue-600 hover:text-blue-800 transition-colors">
                      privacy@aiximo.com
                    </a>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <SafeIcon icon={FiMail} className="h-5 w-5 mr-3 text-blue-600" />
                    <span>Data Protection Officer: dpo@aiximo.com</span>
                  </div>
                </div>
                <div className="text-gray-700 mt-4">
                  <strong>aiximo</strong><br />
                  AI-Powered Development Agency<br />
                  Privacy Department<br />
                  <em>We typically respond within 48 hours</em>
                </div>
              </div>
            </motion.div>

            {/* Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex justify-between items-center mt-12 pt-8 border-t border-gray-200"
            >
              <Link
                to="/terms-of-service"
                className="text-blue-600 hover:text-blue-800 font-medium transition-colors flex items-center"
              >
                <SafeIcon icon={FiShield} className="h-4 w-4 mr-2" />
                Terms of Service
              </Link>
              <Link
                to="/contact"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300"
              >
                Contact Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;